import { useEffect, useRef, useState } from 'react';
import type { NegocioDePlataforma } from '../../api/ops/backoffice';
import { ErrorApi } from '../../api/problema';
import { Chip } from '../../componentes/Chip';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { Paginacion } from '../../componentes/Paginacion';
import { Tabla, type Columna, type FilaTabla } from '../../componentes/Tabla';
import { faltanDosPasos } from '../../logica/acceso';
import { estadoDeError } from '../../logica/estados';
import { usePaginacionCursor } from '../../logica/paginacion';
import { useAnchoVentana } from '../../marco/tamano';
import { SinPermiso } from '../SinPermiso';
import type { PropsSeccion } from '../tipos';
import { Ficha } from './Ficha';
import { CLAVES_FILTRO, FILTROS, chipDeLista, cuota, filtroDe, siglas, textoAvisos } from './logica';

/**
 * **Negocios** del backoffice (1.20): todos los negocios con su estado, plan, cuota, partner y ciudad (G184,
 * `listPlatformBusinesses`), filtros Todos · Activos · En prueba · Con problemas (`with_problems`: avisos abiertos en las
 * colas o la campana, con el triángulo rojo junto al nombre), la lupa (nombre, email o ciudad) y «Anterior / Siguiente»
 * (G121). Cada fila abre la ficha (`?negocio=`) sin entrar en el negocio (G138).
 */
interface Pagina {
    data: NegocioDePlataforma[];
    next_cursor: string | null;
}

export function Negocios(p: PropsSeccion) {
    const { ops, movil, busqueda, cambiarConsulta, onSesionCaducada, onFaltanDosPasos } = p;
    const ancho = useAnchoVentana();
    const filtro = filtroDe(busqueda);
    const [texto, setTexto] = useState('');
    const [buscar, setBuscar] = useState('');
    const [lupa, setLupa] = useState(false);
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;
    const [estado, setEstado] = useState<{ clave: string; pagina: Pagina | null; error: unknown }>({ clave: '', pagina: null, error: null });
    const [vuelta, setVuelta] = useState(0);
    const abierta = busqueda.get('negocio');

    // La búsqueda va a la API (`q`) cuando se deja de escribir.
    useEffect(() => {
        const reloj = setTimeout(() => {
            setBuscar(texto.trim());
        }, 300);
        return () => {
            clearTimeout(reloj);
        };
    }, [texto]);
    const primera = useRef(true);
    useEffect(() => {
        if (primera.current) {
            primera.current = false;
            return;
        }
        reiniciar();
    }, [filtro, buscar, reiniciar]);

    const clave = JSON.stringify([filtro, buscar, pag.cursor, vuelta]);
    useEffect(() => {
        let vivo = true;
        ops.listPlatformBusinesses({ ...(FILTROS[filtro]?.filtro ?? {}), q: buscar === '' ? undefined : buscar, cursor: pag.cursor, limit: 25 })
            .then((pagina) => {
                if (vivo) {
                    setEstado({ clave, pagina, error: null });
                }
            })
            .catch((error: unknown) => {
                if (!vivo) {
                    return;
                }
                if (error instanceof ErrorApi && error.estado === 401) {
                    onSesionCaducada();
                    return;
                }
                if (faltanDosPasos(error)) {
                    onFaltanDosPasos();
                    return;
                }
                setEstado({ clave, pagina: null, error });
            });
        return () => {
            vivo = false;
        };
    }, [ops, filtro, buscar, pag.cursor, vuelta, clave, onSesionCaducada, onFaltanDosPasos]);

    const ancha = ancho >= 1280;
    const columnas: Columna[] = [
        { clave: 'negocio', titulo: 'Negocio', ancho: ancha ? 'minmax(170px, 2fr)' : 'minmax(160px, 2fr)' },
        ...(ancha ? [{ clave: 'ciudad', titulo: 'Ciudad', ancho: 'minmax(80px, 0.8fr)' }] : []),
        { clave: 'estado', titulo: 'Estado', ancho: ancha ? '100px' : '96px' },
        { clave: 'plan', titulo: 'Plan', ancho: ancha ? 'minmax(120px, 1.3fr)' : 'minmax(110px, 1.3fr)' },
        { clave: 'cuota', titulo: 'Cuota', ancho: '116px', alinear: 'derecha' },
        { clave: 'partner', titulo: 'Partner', ancho: ancha ? 'minmax(100px, 1fr)' : 'minmax(90px, 1fr)' },
    ];

    const abrir = (id: string) => {
        cambiarConsulta({ negocio: id, pestana: null });
    };

    const lista = estado.clave === clave ? estado.pagina : null;
    const error = estado.clave === clave ? estado.error : null;
    const filas: FilaTabla[] = (lista?.data ?? []).map((n) => {
        const chip = chipDeLista(n.subscription_status);
        // «2 avisos abiertos» (G1128: `open_alerts_count`) en la etiqueta del triángulo, como el prototipo.
        const avisos = (n.open_alerts_count ?? 0) > 0 ? textoAvisos(n) : n.has_open_alerts === true ? 'Con avisos abiertos' : null;
        const nombre = (
            <span className="rv-bo-negocio__nombre">
                <strong>{n.name}</strong>
                {avisos !== null && <i className="icon-triangle-alert" role="img" aria-label={avisos} title={avisos} />}
            </span>
        );
        return {
            clave: n.id,
            etiqueta: `Abrir la ficha de ${n.name}`,
            onAbrir: () => {
                abrir(n.id);
            },
            celdas: {
                negocio: (
                    <span className="rv-bo-negocio">
                        <span className="rv-bo-siglas">{siglas(n.name)}</span>
                        <span className="rv-bo-negocio__textos">
                            {nombre}
                            <span className="rv-bo-negocio__linea">{ancha ? (n.owner_email ?? '') : [n.city, n.owner_email].filter((x) => x !== null && x !== undefined && x !== '').join(' · ')}</span>
                        </span>
                    </span>
                ),
                ciudad: <span className="rv-bo-celda">{n.city ?? ''}</span>,
                estado: chip === null ? null : <Chip estado={chip.estado}>{chip.texto}</Chip>,
                plan: <span className="rv-bo-celda">{n.plan_summary ?? '—'}</span>,
                cuota: <span className="rv-bo-cuota">{cuota(n.monthly_fee)}</span>,
                partner: <span className="rv-bo-celda">{n.partner_name ?? '—'}</span>,
            },
            tarjeta: (
                <span className="rv-bo-tarjeta-negocio">
                    <span className="rv-bo-siglas">{siglas(n.name)}</span>
                    <span className="rv-bo-negocio__textos" style={{ gap: 4 }}>
                        {nombre}
                        <span className="rv-bo-negocio__linea">{[n.city, n.plan_summary].filter((x) => x !== null && x !== undefined && x !== '').join(' · ')}</span>
                        <span className="rv-bo-tarjeta-negocio__linea2">
                            {chip !== null && <Chip estado={chip.estado}>{chip.texto}</Chip>}
                            <b>{cuota(n.monthly_fee)}</b>
                        </span>
                    </span>
                </span>
            ),
        };
    });

    const sinPermiso = error !== null && estadoDeError(error).tipo === 'sin-permiso';
    return (
        <>
            <div className="rv-bo-herramientas">
                <div className="rv-bo-herramientas__filtros">
                    <Filtros
                        items={FILTROS.map((f) => ({ etiqueta: f.etiqueta }))}
                        valor={filtro}
                        etiqueta="Estado de los negocios"
                        onCambio={(i) => {
                            cambiarConsulta({ filtro: CLAVES_FILTRO[i] ?? null }, true);
                        }}
                    />
                </div>
                <div className="rv-bo-buscar">
                    {lupa || texto !== '' ? (
                        <label className="rv-bo-buscar__caja">
                            <Icono nombre="search" />
                            <input
                                type="search"
                                aria-label="Buscar por nombre, email o ciudad"
                                placeholder="Nombre, email o ciudad"
                                value={texto}
                                autoComplete="off"
                                // Se abre al tocar la lupa: el foco va al campo.
                                autoFocus={lupa}
                                onChange={(e) => {
                                    setTexto(e.target.value);
                                }}
                            />
                            <button
                                type="button"
                                className="rv-bo-buscar__quitar"
                                aria-label="Cerrar la búsqueda"
                                onClick={() => {
                                    setTexto('');
                                    setLupa(false);
                                }}
                            >
                                <Icono nombre="x" />
                            </button>
                        </label>
                    ) : (
                        <button
                            type="button"
                            className="rv-bi rv-bo-lupa"
                            aria-label="Ver los negocios: buscar por nombre, email o ciudad"
                            onClick={() => {
                                setLupa(true);
                            }}
                        >
                            <Icono nombre="search" />
                        </button>
                    )}
                </div>
            </div>
            {sinPermiso ? (
                <SinPermiso seccion="Negocios" superadmin={p.superadmin} movil={movil} onIr={null} />
            ) : error !== null ? (
                <EstadoError
                    seccion="Negocios"
                    codigoSoporte={estadoDeError(error).codigoSoporte}
                    movil={movil}
                    onReintentar={() => {
                        setVuelta((v) => v + 1);
                    }}
                />
            ) : lista === null ? (
                <EstadoCargando seccion="Negocios" />
            ) : (
                <>
                    <Tabla columnas={columnas} filas={filas} movil={movil} etiqueta="Negocios" vacia={<span className="rv-bo-vacia" style={{ display: 'block', boxShadow: 'none' }}>Ningún negocio de esta página coincide.</span>} />
                    <Paginacion
                        hayAnterior={pag.hayAnterior}
                        haySiguiente={typeof lista.next_cursor === 'string' && lista.next_cursor !== ''}
                        movil={movil}
                        onAnterior={pag.anterior}
                        onSiguiente={() => {
                            pag.siguiente(lista.next_cursor);
                        }}
                    />
                </>
            )}
            {abierta !== null && (
                <Ficha
                    key={abierta}
                    {...p}
                    id={abierta}
                    onCerrar={() => {
                        cambiarConsulta({ negocio: null, pestana: null, ventana: null });
                    }}
                    onCambiado={() => {
                        setVuelta((v) => v + 1);
                    }}
                />
            )}
        </>
    );
}
